import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  InputAdornment,
  TextField,
  alpha,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  Divider,
  useTheme,
  useMediaQuery,
  Badge,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  Alert,
} from '@mui/material';
import {
  Search,
  CheckCircle,
  Block,
  Article,
  PowerSettingsNew,
  ReceiptLong,
  FilterList,
  Close as CloseIcon,
  CalendarMonth,
  PersonOutline,
  Business,
  People,
  WhatsApp as WhatsAppIcon,
  Notifications as NotificationsIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  ErrorOutline as ErrorOutlineIcon,
  Person as PersonIcon,
} from '@mui/icons-material';

import { Client, Contact } from '../../types';
import { clientService } from '../../services/clientService';
import SysDateRangePicker from '../../components/SysDateRangePicker';
import ClientesTable from '../../components/client/ClientesTable';
import { enqueueSnackbar } from 'notistack';
import { contactService } from '../../services/contactService';
import mensagemService from '../../services/mensagemService';
import { GatewayWhatsappSelect } from '../../components/selects/GatewayWhatsappSelect';
import { SysEditorWhatsapp } from '../../components/syseditor';

// ─── Tipos ────────────────────────────────────────────────────────────────────

type FiltroStatus = 'todos' | 'ativos' | 'bloqueados' | 'sem_contrato' | 'desativados' | 'sem_faturas';
type FiltroTipoPessoa = 'todos' | 'pf' | 'pj';
type OrdemCampo = 'id' | 'nome' | 'createdAt';
type OrdemDirecao = 'asc' | 'desc';

interface FiltrosState {
  status: FiltroStatus;
  tipoPessoa: FiltroTipoPessoa;
  search: string;
  dataInicio: Date | null;
  dataFim: Date | null;
  page: number;
  rowsPerPage: number;
  orderBy: OrdemCampo;
  order: OrdemDirecao;
}

interface ClientesListarTabProps {
  escritorioId?: string | null;
  notifyTrigger?: number;
  onSelectionChange?: (count: number) => void;
}

// ─── Definições dos filtros rápidos ───────────────────────────────────────────
const FILTROS_RAPIDOS: {
  value: FiltroStatus;
  label: string;
  icon: React.ReactElement;
  color: string;
}[] = [
    { value: 'todos', label: 'Todos', icon: <CheckCircle fontSize="small" />, color: 'success' },
    { value: 'ativos', label: 'Ativos', icon: <CheckCircle fontSize="small" />, color: 'success' },
    { value: 'bloqueados', label: 'Bloqueados', icon: <Block fontSize="small" />, color: 'warning' },
    {
      value: 'sem_contrato',
      label: 'Sem Contrato',
      icon: <Article fontSize="small" />,
      color: 'info',
    },
    {
      value: 'desativados',
      label: 'Desativados',
      icon: <PowerSettingsNew fontSize="small" />,
      color: 'default',
    },
    {
      value: 'sem_faturas',
      label: 'Sem Faturas',
      icon: <ReceiptLong fontSize="small" />,
      color: 'secondary',
    },
  ];

// ─── Componente principal ──────────────────────────────────────────────────────
const ClientesListarTab: React.FC<ClientesListarTabProps> = ({ 
  escritorioId,
  notifyTrigger,
  onSelectionChange,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // ── Estado dos filtros ──────────────────────────────────────────────────────
  const [filtros, setFiltros] = useState<FiltrosState>({
    status: 'todos',
    tipoPessoa: 'todos',
    search: '',
    dataInicio: null,
    dataFim: null,
    page: 0,
    rowsPerPage: 30,
    orderBy: 'createdAt',
    order: 'desc',
  });

  // Seleção e Notificações
  const [selectedClientIds, setSelectedClientIds] = useState<Set<number>>(new Set());
  const [notifyModalOpen, setNotifyModalOpen] = useState(false);
  const [notifyMensagem, setNotifyMensagem] = useState('');
  const [notifyGateway, setNotifyGateway] = useState<number | ''>('');
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [notifyStep, setNotifyStep] = useState<'form' | 'sending' | 'results'>('form');
  const [notifyResults, setNotifyResults] = useState<{ clientName: string; success: boolean; error?: string }[]>([]);
  const [notifyProgress, setNotifyProgress] = useState({ current: 0, total: 0, currentClientName: '' });

  // Debounce do campo de busca para evitar requisições excessivas
  const [searchDebounced, setSearchDebounced] = useState('');
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setSearchDebounced(filtros.search);
      // Volta para página 0 ao mudar a busca
      setFiltros((prev) => ({ ...prev, page: 0 }));
    }, 400);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [filtros.search]);

  // ── Montagem dos params para a query ───────────────────────────────────────
  const queryParams = {
    page: filtros.page + 1, // API usa 1-indexed
    limit: filtros.rowsPerPage,
    sortBy: filtros.orderBy,
    sortOrder: filtros.order.toUpperCase() as 'ASC' | 'DESC',
    search: searchDebounced || undefined,
    filtro_status: filtros.status,
    tipo_pessoa: filtros.tipoPessoa !== 'todos' ? filtros.tipoPessoa : undefined,
    data_inicio: filtros.dataInicio ? filtros.dataInicio.toISOString().split('T')[0] : undefined,
    data_fim: filtros.dataFim ? filtros.dataFim.toISOString().split('T')[0] : undefined,
    ...(escritorioId ? { escritorio_id: escritorioId } : {}),
  };

  // ── Query server-side ──────────────────────────────────────────────────────
  const { data: response, isLoading, isFetching, error } = useQuery({
    queryKey: ['clientes-listar-tab', queryParams],
    queryFn: () => clientService.getAll(queryParams as any),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  const clientes: Client[] = (response as any)?.data ?? [];
  const totalRegistros: number =
    (response as any)?.pagination?.total ?? clientes.length;

  const selectedClientesList = clientes.filter(c => selectedClientIds.has(c.id));

  // ── Helpers de seleção ───────────────────────────────────────────────────────
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedClientIds(new Set(clientes.map((c) => c.id)));
    } else {
      setSelectedClientIds(new Set());
    }
  };

  const handleSelectRow = (id: number, checked: boolean) => {
    setSelectedClientIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(selectedClientIds.size);
    }
  }, [selectedClientIds, onSelectionChange]);

  useEffect(() => {
    if (notifyTrigger && notifyTrigger > 0 && selectedClientIds.size > 0) {
      handleOpenNotifyModal();
    }
  }, [notifyTrigger]);

  // ── Helpers de Notificação ───────────────────────────────────────────────────
  const handleOpenNotifyModal = () => {
    setNotifyMensagem('');
    setNotifyGateway(9999);
    setNotifyResults([]);
    setNotifyStep('form');
    setNotifyProgress({ current: 0, total: 0, currentClientName: '' });
    setNotifyModalOpen(true);
  };

  const handleCloseNotifyModal = () => {
    if (!notifyLoading) {
      setNotifyModalOpen(false);
    }
  };

  const handleSendBulkNotifications = async () => {
    if (!notifyMensagem.trim()) {
      enqueueSnackbar('Digite uma mensagem para enviar', { variant: 'warning' });
      return;
    }
    if (!notifyGateway) {
      enqueueSnackbar('Selecione um gateway WhatsApp', { variant: 'warning' });
      return;
    }
    if (selectedClientesList.length === 0) {
      enqueueSnackbar('Nenhum cliente selecionado', { variant: 'warning' });
      return;
    }

    setNotifyLoading(true);
    setNotifyStep('sending');
    const results: { clientName: string; success: boolean; error?: string }[] = [];

    setNotifyProgress({ current: 0, total: selectedClientesList.length, currentClientName: '' });

    let count = 0;
    for (const client of selectedClientesList) {
      count++;
      setNotifyProgress({ current: count, total: selectedClientesList.length, currentClientName: client.nome || '' });

      try {
        const contactsResponse = await contactService.getByClientId(client.id, {
          limit: 100,
          filters: { is_whatsapp: true, tipo: 'celular', recebe_notificacao: true }
        });

        const whatsappContacts = (contactsResponse.data || []).filter(
          (c: Contact) => c.is_whatsapp && c.tipo === 'celular' && c.recebe_notificacao
        );

        if (whatsappContacts.length === 0) {
          results.push({ clientName: client.nome || '', success: false, error: 'Sem contato WhatsApp ativo' });
          continue;
        }

        for (const contact of whatsappContacts) {
          try {
            await mensagemService.createMensagem({
              tipo: 'whatsapp',
              destino: contact.id,
              conteudo: notifyMensagem.trim(),
              cliente_id: client.id,
              gateway_id: notifyGateway === 9999 ? undefined : Number(notifyGateway)
            });
            results.push({ clientName: `${client.nome} (${contact.nome || contact.numero})`, success: true });
          } catch (err: any) {
            results.push({ clientName: `${client.nome} (${contact.nome || contact.numero})`, success: false, error: err.message || 'Erro ao cadastrar' });
          }
        }
      } catch (err: any) {
        results.push({ clientName: client.nome || '', success: false, error: err.message || 'Erro ao buscar contatos' });
      }
    }

    setNotifyResults(results);
    setNotifyStep('results');
    setNotifyLoading(false);

    const successCount = results.filter((r) => r.success && !r.error).length;
    if (successCount > 0) {
      enqueueSnackbar(`${successCount} mensagem(ns) cadastrada(s) com sucesso!`, { variant: 'success' });
    }
  };

  // ── Helpers de atualização de estado (reset da paginação) ──────────────────
  const setFiltroComResetPagina = useCallback(
    (parcial: Partial<FiltrosState>) => {
      setFiltros((prev) => ({ ...prev, ...parcial, page: 0 }));
    },
    []
  );

  const handleStatusChange = (_: React.MouseEvent<HTMLElement>, novoStatus: FiltroStatus | null) => {
    if (!novoStatus) return; // manter pelo menos um selecionado
    setFiltroComResetPagina({ status: novoStatus });
  };

  const handleTipoPessoaChange = (_: React.MouseEvent<HTMLElement>, novoTipo: FiltroTipoPessoa | null) => {
    if (!novoTipo) return;
    setFiltroComResetPagina({ tipoPessoa: novoTipo });
  };

  const handleSort = (campo: OrdemCampo) => {
    const isAsc = filtros.orderBy === campo && filtros.order === 'asc';
    setFiltroComResetPagina({ orderBy: campo, order: isAsc ? 'desc' : 'asc' });
  };



  const handleDateChange = ({ startDate, endDate }: { startDate: Date | null; endDate: Date | null }) => {
    setFiltroComResetPagina({ dataInicio: startDate, dataFim: endDate });
  };

  const temFiltroDatas = filtros.dataInicio || filtros.dataFim;

  // ── Status real do cliente (considera contratos bloqueados) ────────────────
  const getStatusReal = (client: Client): string => {
    if ((client.contratos ?? []).some((c) => c.status === 'bloqueado')) return 'bloqueado';
    return client.status;
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ pb: 6 }}>
      {/* ── Filtros ── */}
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          mb: 1.5,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {/* Linha superior: filtros rápidos de status */}
        <Box
          sx={{
            px: 1.5,
            pt: 1,
            pb: 0.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.04)} 0%, transparent 100%)`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 0.5 }}>
            <FilterList sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="caption" fontWeight={600} color="text.secondary">
              Filtrar por:
            </Typography>
          </Box>

          <ToggleButtonGroup
            value={filtros.status}
            exclusive
            onChange={handleStatusChange}
            size="small"
            sx={{
              flexWrap: 'wrap',
              gap: 0.5,
              '& .MuiToggleButtonGroup-grouped': {
                border: '1px solid',
                '&:not(:first-of-type)': { borderRadius: 1.5, ml: 0 },
                '&:first-of-type': { borderRadius: 1.5 },
              },
            }}
          >
            {FILTROS_RAPIDOS.map((f) => {
              // Obtém a string de cor .main do objeto de paleta (ex: theme.palette.success.main)
              const paletteEntry = theme.palette[f.color as keyof typeof theme.palette] as any;
              const colorMain: string =
                typeof paletteEntry === 'object' && paletteEntry?.main
                  ? paletteEntry.main
                  : typeof paletteEntry === 'string'
                    ? paletteEntry
                    : '#9e9e9e'; // fallback cinza

              const isSelected = filtros.status === f.value;

              return (
                <ToggleButton
                  key={f.value}
                  value={f.value}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    px: 1.5,
                    py: 0.4,
                    borderRadius: 1.5,
                    gap: 0.5,
                    border: `1px solid ${colorMain} !important`,
                    color: isSelected ? 'white' : colorMain,
                    bgcolor: isSelected ? colorMain : alpha(colorMain, 0.05),
                    '&:hover': {
                      bgcolor: isSelected ? alpha(colorMain, 0.85) : alpha(colorMain, 0.14),
                      color: isSelected ? 'white' : colorMain,
                    },
                    '&.Mui-selected': {
                      bgcolor: colorMain,
                      color: 'white',
                      '&:hover': {
                        bgcolor: alpha(colorMain, 0.85),
                      },
                    },
                  }}
                >
                  {f.icon}
                  <span>
                    {f.label}
                    {isSelected && ` ${totalRegistros}`}
                  </span>
                </ToggleButton>
              );
            })}
          </ToggleButtonGroup>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 0.5 }}>
            <People sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="caption" fontWeight={600} color="text.secondary">
              Tipo:
            </Typography>
          </Box>

          <ToggleButtonGroup
            value={filtros.tipoPessoa}
            exclusive
            onChange={handleTipoPessoaChange}
            size="small"
            sx={{
              gap: 0.5,
              '& .MuiToggleButtonGroup-grouped': {
                border: '1px solid',
                '&:not(:first-of-type)': { borderRadius: 1.5, ml: 0 },
                '&:first-of-type': { borderRadius: 1.5 },
              },
            }}
          >
            {[
              { value: 'todos' as FiltroTipoPessoa, label: 'Todos', icon: <People fontSize="small" />, color: '#9e9e9e' },
              { value: 'pf' as FiltroTipoPessoa, label: 'Pessoa Física', icon: <PersonOutline fontSize="small" />, color: theme.palette.info.main },
              { value: 'pj' as FiltroTipoPessoa, label: 'Pessoa Jurídica', icon: <Business fontSize="small" />, color: theme.palette.warning.main },
            ].map((f) => {
              const isSelected = filtros.tipoPessoa === f.value;
              return (
                <ToggleButton
                  key={f.value}
                  value={f.value}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    px: 1.5,
                    py: 0.4,
                    borderRadius: 1.5,
                    gap: 0.5,
                    border: `1px solid ${f.color} !important`,
                    color: isSelected ? 'white' : f.color,
                    bgcolor: isSelected ? f.color : alpha(f.color, 0.05),
                    '&:hover': {
                      bgcolor: isSelected ? alpha(f.color, 0.85) : alpha(f.color, 0.14),
                      color: isSelected ? 'white' : f.color,
                    },
                    '&.Mui-selected': {
                      bgcolor: f.color,
                      color: 'white',
                      '&:hover': {
                        bgcolor: alpha(f.color, 0.85),
                      },
                    },
                  }}
                >
                  {f.icon}
                  <span>{f.label}</span>
                </ToggleButton>
              );
            })}
          </ToggleButtonGroup>
        </Box>

        <Divider />

        {/* Linha inferior: busca + filtro de data */}
        <Box
          sx={{
            px: 1.5,
            py: 0.75,
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          {/* Campo de busca */}
          <TextField
            placeholder="Buscar por nome, CPF/CNPJ, endereço, bairro, cidade, estado, telefone, email..."
            value={filtros.search}
            onChange={(e) => setFiltros((prev) => ({ ...prev, search: e.target.value }))}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: filtros.search ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setFiltros((prev) => ({ ...prev, search: '', page: 0 }))}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              flex: '1 1 280px',
              maxWidth: 560,
              '& .MuiOutlinedInput-root': { height: 34, fontSize: '0.85rem' },
            }}
          />

          {/* Filtro de data de cadastro */}
          <Box
            sx={{
              flex: '1 1 220px',
              maxWidth: 320,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <Badge
              color="primary"
              variant="dot"
              invisible={!temFiltroDatas}
              sx={{ flex: 1 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
                <CalendarMonth sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
                <Box sx={{ flex: 1 }}>
                  <SysDateRangePicker
                    value={{ startDate: filtros.dataInicio, endDate: filtros.dataFim }}
                    onChange={handleDateChange}
                  />
                </Box>
              </Box>
            </Badge>
          </Box>

          {/* Indicador de loading ou ações de seleção */}
          {(isLoading || isFetching) ? (
            <CircularProgress size={20} thickness={4} sx={{ ml: 'auto' }} />
          ) : (
            <Box sx={{ ml: 'auto' }} />
          )}
        </Box>
      </Paper>

      {/* ── Tabela ── */}
      <ClientesTable
        clientes={clientes}
        totalRegistros={totalRegistros}
        loading={isLoading}
        error={!!error}
        serverSide
        page={filtros.page}
        rowsPerPage={filtros.rowsPerPage}
        onPageChange={(p) => setFiltros((prev) => ({ ...prev, page: p }))}
        onRowsPerPageChange={(rpp) => setFiltros((prev) => ({ ...prev, rowsPerPage: rpp, page: 0 }))}
        orderBy={filtros.orderBy}
        order={filtros.order}
        onSort={handleSort}
        selectable
        selectedIds={selectedClientIds}
        onSelectAll={handleSelectAll}
        onSelectRow={handleSelectRow}
        isFetching={isFetching}
      />

      {/* Modal de Notificação em Massa */}
      <Dialog
        open={notifyModalOpen}
        onClose={handleCloseNotifyModal}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={notifyLoading}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <WhatsAppIcon color="success" />
              <Typography variant="h6" fontWeight="bold">
                Notificar Clientes em Massa
              </Typography>
            </Box>
            <IconButton onClick={handleCloseNotifyModal} size="small" disabled={notifyLoading}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {selectedClientesList.length} cliente(s) selecionado(s)
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          {notifyStep === 'form' ? (
            <Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Clientes selecionados:
              </Typography>
              <Box
                sx={{
                  maxHeight: 120,
                  overflowY: 'auto',
                  mb: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  bgcolor: alpha(theme.palette.background.default, 0.5),
                }}
              >
                <List dense disablePadding>
                  {selectedClientesList.map((client) => (
                    <ListItem key={client.id} sx={{ py: 0.25 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <PersonIcon fontSize="small" color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={client.nome}
                        secondary={`ID: #${client.id}`}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>

              <Box sx={{ mb: 2 }}>
                <GatewayWhatsappSelect
                  value={notifyGateway}
                  onChange={setNotifyGateway}
                  label="Gateway WhatsApp"
                  required
                  disabled={notifyLoading}
                  size="small"
                  helperText="Selecione o gateway para envio das mensagens"
                />
              </Box>

              <Box>
                <SysEditorWhatsapp
                  value={notifyMensagem}
                  onChange={setNotifyMensagem}
                  placeholder="Digite a mensagem que será enviada para todos os clientes selecionados..."
                  maxLength={1000}
                  disabled={notifyLoading}
                  showPreview={true}
                  rows={4}
                  label="Mensagem *"
                  helperText="Use formatação *negrito* e _itálico_ para destacar texto"
                />
              </Box>
            </Box>
          ) : notifyStep === 'sending' ? (
            <Box sx={{ py: 3 }}>
              <Typography variant="body1" fontWeight={600} align="center" sx={{ mb: 2 }}>
                Processando clientes...
              </Typography>
              <LinearProgress
                variant="determinate"
                value={notifyProgress.total > 0 ? (notifyProgress.current / notifyProgress.total) * 100 : 0}
                sx={{ height: 8, borderRadius: 4, mb: 2 }}
              />
              <Typography variant="body2" color="text.secondary" align="center">
                Cliente {notifyProgress.current} de {notifyProgress.total}
              </Typography>
              <Typography variant="body2" fontWeight={500} align="center" sx={{ mt: 0.5 }}>
                {notifyProgress.currentClientName}
              </Typography>
            </Box>
          ) : (
            <Box>
              <Alert
                severity={notifyResults.every((r) => r.success) ? 'success' : notifyResults.some((r) => r.success) ? 'warning' : 'error'}
                sx={{ mb: 2 }}
              >
                {notifyResults.filter((r) => r.success && !r.error).length} mensagem(ns) cadastrada(s)
                {notifyResults.filter((r) => r.success && r.error).length > 0 &&
                  `, ${notifyResults.filter((r) => r.success && r.error).length} com avisos`}
                {notifyResults.filter((r) => !r.success).length > 0 &&
                  `, ${notifyResults.filter((r) => !r.success).length} falha(s)`}
              </Alert>

              <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                <List dense>
                  {notifyResults.map((result, index) => (
                    <ListItem key={index} sx={{ py: 0.15 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        {result.success && !result.error ? (
                          <CheckCircleOutlineIcon fontSize="small" color="success" />
                        ) : result.success && result.error ? (
                          <CheckCircleOutlineIcon fontSize="small" color="info" />
                        ) : (
                          <ErrorOutlineIcon fontSize="small" color="error" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" component="span" fontWeight={500}>
                            {result.clientName}
                          </Typography>
                        }
                        secondary={result.success ? (result.error || 'Mensagem cadastrada') : result.error}
                        secondaryTypographyProps={{
                          variant: 'caption',
                          color: result.success ? (result.error ? 'info.main' : 'success.main') : 'error.main'
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          {notifyStep === 'form' ? (
            <>
              <Button onClick={handleCloseNotifyModal} disabled={notifyLoading}>
                Cancelar
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleSendBulkNotifications}
                disabled={notifyLoading || !notifyMensagem.trim() || !notifyGateway || selectedClientesList.length === 0}
                startIcon={<NotificationsIcon />}
              >
                Cadastrar Mensagens
              </Button>
            </>
          ) : notifyStep === 'sending' ? (
            <Button disabled>
              Processando...
            </Button>
          ) : (
            <Button variant="contained" onClick={handleCloseNotifyModal}>
              Fechar
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientesListarTab;

